import { useCallback, useEffect, useRef, useState } from 'react';
import { api, ApiError, submitAssignment, type AssignmentView } from '../api';

/**
 * The trainee's side of an assignment: the brief, a box to write in, a file to
 * attach, and — once an instructor has read it — the mark and their comments.
 */
export function AssignmentUnit({ courseId, unitId, onFinished }: {
  courseId: number; unitId: number; onFinished: () => void;
}) {
  const [view, setView] = useState<AssignmentView | null>(null);
  const [text, setText] = useState('');
  const [file, setFile] = useState<File | null>(null);
  const [pct, setPct] = useState<number | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const picker = useRef<HTMLInputElement>(null);

  const load = useCallback(async () => {
    try { setView(await api.get<AssignmentView>(`/courses/${courseId}/units/${unitId}/assignment`)); }
    catch (e) { setError((e as ApiError).message); }
  }, [courseId, unitId]);
  useEffect(() => { void load(); }, [load]);

  if (error && !view) return <p className="auth__error">{error}</p>;
  if (!view) return <p className="muted">Loading…</p>;

  const latest = view.submissions[0] ?? null;

  const hand = async () => {
    setBusy(true); setError(null); setPct(0);
    try {
      await submitAssignment(courseId, unitId, { text: text.trim() || undefined, file }, setPct);
      setText(''); setFile(null);
      if (picker.current) picker.current.value = '';
      await load();
      onFinished();
    } catch (e) { setError((e as ApiError).message); }
    finally { setBusy(false); setPct(null); }
  };

  return (
    <>
      {view.unit.promptHtml && <div className="prose" dangerouslySetInnerHTML={{ __html: view.unit.promptHtml }} />}

      {!view.unlocked && <p className="notice">Finish the earlier units before handing this in.</p>}

      {latest && (
        <section className="panel">
          <h3 className="h3">Your hand-in</h3>
          <p className="muted small">Handed in {new Date(latest.submittedAt).toLocaleString()}</p>
          {latest.text && <p className="submission">{latest.text}</p>}
          {latest.mediaFileId && (
            <p><a href={`/api/media/${latest.mediaFileId}`} target="_blank" rel="noreferrer">Open the file you attached</a></p>
          )}

          {latest.gradedAt ? (
            <div className={latest.passed ? 'result result--pass' : 'result result--fail'}>
              <strong>{latest.grade}%</strong> — {latest.passed ? 'passed' : 'not passed'}
              {latest.feedback && <p className="prose">{latest.feedback}</p>}
              {!latest.passed && <p className="muted small">You can hand in again below.</p>}
            </div>
          ) : (
            <p className="notice">Waiting to be marked by your instructor.</p>
          )}
        </section>
      )}

      {view.canSubmit && (
        <section className="panel">
          <h3 className="h3">{latest ? 'Hand in again' : 'Hand in'}</h3>
          <div className="form">
            <label><span>Your answer</span>
              <textarea rows={8} value={text} onChange={(e) => setText(e.target.value)}
                placeholder="Type your answer here, or attach a file below." /></label>
            <label><span>Attach a file (optional)</span>
              <input ref={picker} type="file" onChange={(e) => setFile(e.target.files?.[0] ?? null)} /></label>
            <p className="muted small">{view.accepts}</p>
            {pct != null && <p className="muted small">Uploading… {pct}%</p>}
            {error && <p className="auth__error">{error}</p>}
            <div className="row">
              <button className="btn btn--primary" disabled={busy || (!text.trim() && !file)} onClick={() => void hand()}>
                {busy ? 'Sending…' : 'Hand in'}
              </button>
            </div>
          </div>
        </section>
      )}

      {!view.canSubmit && !latest && view.unlocked && (
        <p className="muted">This assignment is not open for hand-ins.</p>
      )}

      {view.submissions.length > 1 && (
        <section className="panel">
          <h3 className="h3">Earlier hand-ins</h3>
          <ul className="plain">
            {view.submissions.slice(1).map((sub) => (
              <li key={sub.id}>
                {new Date(sub.submittedAt).toLocaleDateString()} —{' '}
                {sub.grade == null ? 'not marked' : `${sub.grade}% ${sub.passed ? 'passed' : 'not passed'}`}
                {sub.feedback && <span className="muted"> · {sub.feedback}</span>}
              </li>
            ))}
          </ul>
        </section>
      )}
    </>
  );
}
